// "Exportar e importar" de Tienda online, en su propia pantalla, como en Tiendanube
// (ADR-2026-10-30-exportar-e-importar-en-su-pantalla). Exportar baja los productos de la tienda en un Excel
// (con la búsqueda y los filtros que traía la lista, si los había); importar sube una planilla y el asistente
// arma los productos en la lista de cambios de la tienda: se crean cuando la persona sincroniza.
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AlertTriangle, ArrowDownToLine, ArrowLeft, Check, FileSpreadsheet, Loader2, Plus, Upload, X } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useBrandProfile } from '../../hooks/useBrandProfile';
import { escribirXlsx, leerXlsx } from '../../utils/planilla/xlsx';
import { parsearCsv } from '../../utils/planilla/csv';
import { analizarPlanilla, armarParaImportar, type PlanillaAnalizada } from './importarPlanilla';
import { boton } from './botones';
import { correrImportacion, type PasoDeImportacion, type ResultadoDeImportacion } from './correrImportacion';
import {
  cuantosFiltros,
  fetchProductosDeLaTienda,
  type FiltrosDeLaTienda,
  type PlataformaDeTienda,
  type ProductoDeLaTienda,
} from './productosDeLaTienda';

/** Lo que trae la lista al abrir esta pantalla: la tienda que se estaba viendo y sus filtros. */
export interface DesdeLaLista {
  plataforma: PlataformaDeTienda;
  q?: string;
  categoria?: string | null;
  filtros?: FiltrosDeLaTienda;
  /**
   * Los filtros que la lista aplica sobre lo cargado y no le pide a la tienda (review Greptile de #232): la
   * búsqueda en Mercado Libre, "Sin categoría", "Sin sincronizar" (los ids que lo están) y una categoría que
   * todavía no existe en la tienda (ahí no hay productos de la tienda para exportar).
   */
  buscarAca?: string;
  sinCategoria?: boolean;
  soloSinSincronizar?: string[];
  categoriaSinTienda?: boolean;
}

/** Hasta 400 páginas (20.000 productos). Más que eso no se lee: se avisa, no se trabaja con una parte. */
const MAX_PAGINAS = 400;

/** Todas las páginas. Si la tienda tiene más que el tope, tira: con una parte, uno que está se tomaría por nuevo. */
async function todasLasPaginas(pedir: (pagina: number) => ReturnType<typeof fetchProductosDeLaTienda>, vivo = () => true) {
  const todos: ProductoDeLaTienda[] = [];
  for (let pagina = 1; pagina <= MAX_PAGINAS; pagina++) {
    const r = await pedir(pagina);
    todos.push(...r.productos);
    if (!r.hayMas || !vivo()) return todos;
  }
  throw new Error('demasiados');
}

/** Los filtros locales de la lista, sobre lo que se leyó de la tienda. */
function conLosFiltrosDeLaLista(productos: ProductoDeLaTienda[], d: DesdeLaLista | null): ProductoDeLaTienda[] {
  if (!d) return productos;
  if (d.categoriaSinTienda) return [];
  const q = d.buscarAca?.trim().toLowerCase();
  const pendientes = d.soloSinSincronizar ? new Set(d.soloSinSincronizar) : null;
  return productos.filter(
    (p) =>
      (!q || p.nombre.toLowerCase().includes(q) || p.idExterno.toLowerCase().includes(q)) &&
      (!d.sinCategoria || p.categorias.length === 0) &&
      (!pendientes || pendientes.has(p.idExterno)),
  );
}

const descargar = (bytes: Uint8Array, nombre: string) => {
  const url = URL.createObjectURL(new Blob([bytes], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = nombre;
  a.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
};

export default function ExportarImportar() {
  const navigate = useNavigate();
  const desde = (useLocation().state ?? null) as DesdeLaLista | null;
  const plataforma: PlataformaDeTienda = desde?.plataforma ?? 'tiendanube';
  const { profile, currentBrandId } = useBrandProfile();
  const companyId = profile?.id_company ?? null;
  const [pestana, setPestana] = useState<'exportar' | 'importar'>('exportar');

  return (
    <div className="mx-auto max-w-4xl pt-1 pb-10 px-4 md:py-10 md:px-8">
      <button
        type="button"
        onClick={() => navigate('/lanzamientos')}
        className="-ml-1 inline-flex items-center gap-1.5 rounded-lg py-1 px-1 text-sm font-medium text-neutral-500 transition-colors hover:text-brand-black"
      >
        <ArrowLeft className="h-4 w-4" />
        Tienda online
      </button>
      <h1 className="mt-3 text-[26px] font-semibold leading-tight tracking-[-0.01em] text-brand-black sm:text-2xl">Exportar e importar</h1>
      <p className="mt-1 text-sm text-neutral-500">Cambiá o sumá muchos productos a la vez con una planilla de Excel.</p>

      <div role="tablist" aria-label="Exportar o importar" className="relative mt-6 grid grid-cols-2 border-b border-neutral-200">
        {(['exportar', 'importar'] as const).map((p) => (
          <button
            key={p}
            type="button"
            role="tab"
            aria-selected={pestana === p}
            onClick={() => setPestana(p)}
            className={`pb-3 text-sm font-medium transition-colors ${pestana === p ? 'text-brand-primary' : 'text-neutral-500 hover:text-brand-black'}`}
          >
            {p === 'exportar' ? 'Exportar' : 'Importar'}
          </button>
        ))}
        {/* La raya azul se desliza a la pestaña elegida. */}
        <span
          aria-hidden="true"
          className={`absolute -bottom-px left-0 h-0.5 w-1/2 rounded-full bg-brand-primary transition-transform duration-300 ease-out ${
            pestana === 'importar' ? 'translate-x-full' : 'translate-x-0'
          }`}
        />
      </div>

      {pestana === 'exportar' ? (
        <Exportar plataforma={plataforma} brandId={currentBrandId} desde={desde} />
      ) : (
        <Importar companyId={companyId} brandId={currentBrandId} />
      )}
    </div>
  );
}

function Exportar({ plataforma, brandId, desde }: { plataforma: PlataformaDeTienda; brandId: string | null; desde: DesdeLaLista | null }) {
  const traiaFiltros =
    !!desde &&
    (!!desde.q ||
      !!desde.categoria ||
      cuantosFiltros(desde.filtros ?? {}) > 0 ||
      !!desde.buscarAca ||
      !!desde.sinCategoria ||
      !!desde.soloSinSincronizar ||
      !!desde.categoriaSinTienda);
  const [conFiltros, setConFiltros] = useState(traiaFiltros);
  const [lectura, setLectura] = useState<
    { estado: 'cargando' } | { estado: 'error'; demasiados: boolean } | { estado: 'listo'; productos: ProductoDeLaTienda[] }
  >({
    estado: 'cargando',
  });
  const [descargando, setDescargando] = useState(false);

  // Se leen todos al entrar (para decir cuántos son) y se exportan esos mismos.
  useEffect(() => {
    let vivo = true;
    setLectura({ estado: 'cargando' });
    void (async () => {
      try {
        const todos = await todasLasPaginas(
          (pagina) =>
            fetchProductosDeLaTienda({
              brandId,
              plataforma,
              pagina,
              ...(conFiltros && desde ? { q: desde.q, categoria: desde.categoria, filtros: desde.filtros } : {}),
            }),
          () => vivo,
        );
        if (vivo) setLectura({ estado: 'listo', productos: conFiltros ? conLosFiltrosDeLaLista(todos, desde) : todos });
      } catch (e) {
        if (vivo) setLectura({ estado: 'error', demasiados: e instanceof Error && e.message === 'demasiados' });
      }
    })();
    return () => {
      vivo = false;
    };
  }, [brandId, plataforma, conFiltros, desde]);

  const exportar = () => {
    if (lectura.estado !== 'listo') return;
    setDescargando(true);
    const estado = { publicado: 'Visible', oculto: 'Oculto', pausado: 'Pausado' } as const;
    const filas = [
      ['Nombre', 'ID en la tienda', 'Precio', 'Precio promocional', 'Stock', 'Variantes', 'Estado', 'Link'],
      ...lectura.productos.map((p) => [
        p.nombre,
        p.idExterno,
        p.precio != null ? String(p.precio) : '',
        p.precioPromocional != null ? String(p.precioPromocional) : '',
        p.stock == null ? 'Sin límite' : String(p.stock),
        String(p.variantes),
        estado[p.estado],
        p.url ?? '',
      ]),
    ];
    descargar(escribirXlsx([{ nombre: 'Productos', filas }]), `productos-${plataforma}-${new Date().toISOString().slice(0, 10)}.xlsx`);
    setDescargando(false);
  };

  return (
    <div className="mt-6 rounded-2xl border border-neutral-200 bg-surface-card p-5 shadow-sm sm:p-6">
      <p className="text-sm text-neutral-500">Productos a exportar</p>
      {lectura.estado === 'cargando' ? (
        <div className="mt-2 h-10 w-20 animate-pulse rounded-lg bg-neutral-100" aria-busy="true" />
      ) : lectura.estado === 'error' ? (
        <p role="alert" className="mt-2 text-sm text-red-600">
          {lectura.demasiados
            ? 'Tu tienda tiene más de 20.000 productos: para exportarlos, usá los filtros de la lista.'
            : 'No pudimos leer los productos de tu tienda. Probá de nuevo en un rato.'}
        </p>
      ) : (
        <p className="mt-1 text-4xl font-semibold tabular-nums text-brand-black">{lectura.productos.length.toLocaleString('es-AR')}</p>
      )}

      {traiaFiltros && (
        <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-[rgb(var(--brand-primary-rgb)/0.07)] py-1.5 pl-3 pr-1.5 text-xs font-medium text-brand-primary">
          {conFiltros ? 'Con la búsqueda y los filtros de la lista' : 'Todos los productos'}
          <button
            type="button"
            onClick={() => setConFiltros((v) => !v)}
            className="inline-flex items-center gap-1 rounded-full bg-white py-0.5 px-2 text-brand-primary shadow-sm hover:opacity-80"
          >
            {conFiltros ? (
              <>
                <X className="h-3 w-3" /> Quitar
              </>
            ) : (
              'Usar filtros'
            )}
          </button>
        </div>
      )}

      <p className="mt-4 text-xs text-neutral-500">
        Un Excel con nombre, precio, precio promocional, stock, variantes, estado y link de cada producto.
      </p>
      <button
        type="button"
        onClick={exportar}
        disabled={lectura.estado !== 'listo' || lectura.productos.length === 0 || descargando}
        className={boton('primario', { extra: 'mt-5' })}
      >
        {descargando ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowDownToLine className="h-4 w-4" />}
        Exportar
      </button>
    </div>
  );
}

/** Las filas de un Excel (primera hoja) o de un .csv. */
async function leerFilas(archivo: File): Promise<string[][]> {
  if (/\.xlsx$/i.test(archivo.name)) return leerXlsx(new Uint8Array(await archivo.arrayBuffer()))[0]?.filas ?? [];
  return parsearCsv(await archivo.text());
}

/** Todos los productos de Tiendanube, para saber cuáles de la planilla ya están. */
async function todosLosDeTiendanube(brandId: string | null): Promise<ProductoDeLaTienda[]> {
  return todasLasPaginas((pagina) => fetchProductosDeLaTienda({ brandId, plataforma: 'tiendanube', pagina }));
}

const plural = (n: number, uno: string, varios: string) => `${n} ${n === 1 ? uno : varios}`;

function Importar({ companyId, brandId }: { companyId: string | null; brandId: string | null }) {
  const navigate = useNavigate();
  const entrada = useRef<HTMLInputElement>(null);
  const [archivo, setArchivo] = useState<File | null>(null);
  const [analisis, setAnalisis] = useState<
    { estado: 'leyendo' } | { estado: 'error'; texto: string } | { estado: 'listo'; planilla: PlanillaAnalizada<ProductoDeLaTienda> } | null
  >(null);
  const [arrastrando, setArrastrando] = useState(false);
  const [modificar, setModificar] = useState(true);
  const [stock, setStock] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /** Cada archivo elegido tiene su número: lo que llega de uno anterior (cambiado o quitado) se descarta. */
  const lecturaDeArchivo = useRef(0);
  const elegir = (f: File | undefined) => {
    if (!f) return;
    if (!/\.(xlsx|csv|tsv)$/i.test(f.name)) {
      setError('Tiene que ser un Excel (.xlsx) o un .csv.');
      return;
    }
    setError(null);
    setArchivo(f);
    // Se lee y se cruza con la tienda enseguida: así las casillas dicen qué va a pasar con cada producto.
    setAnalisis({ estado: 'leyendo' });
    const esta = ++lecturaDeArchivo.current;
    void Promise.all([leerFilas(f), todosLosDeTiendanube(brandId)])
      .then(([filas, tienda]) => {
        if (esta !== lecturaDeArchivo.current) return;
        const planilla = analizarPlanilla(filas, tienda);
        setAnalisis(
          planilla.productos.length === 0
            ? { estado: 'error', texto: 'La planilla no tiene productos (la primera fila tiene que ser el encabezado).' }
            : { estado: 'listo', planilla },
        );
      })
      .catch((e) => {
        if (esta !== lecturaDeArchivo.current) return;
        setAnalisis({
          estado: 'error',
          texto:
            e instanceof Error && e.message === 'demasiados'
              ? 'Tu tienda tiene más de 20.000 productos: no podemos compararla entera con la planilla.'
              : 'No pudimos leer la planilla o los productos de tu tienda.',
        });
      });
  };
  const quitar = () => {
    lecturaDeArchivo.current++;
    setArchivo(null);
    setAnalisis(null);
  };

  const plan = analisis?.estado === 'listo' ? armarParaImportar(analisis.planilla, { modificar, stock }) : null;
  const yaEstan = analisis?.estado === 'listo' ? analisis.planilla.productos.filter((p) => p.existente).length : 0;
  const ambiguos = analisis?.estado === 'listo' ? analisis.planilla.productos.filter((p) => p.ambiguo).map((p) => p.nombre) : [];
  const sinStock = analisis?.estado === 'listo' && analisis.planilla.columnaStock < 0;
  const hayQueHacer = !!plan && (plan.cuantosNuevos > 0 || plan.existentes.length > 0);

  /** La importación corre acá, sin IA (`correrImportacion`); la pantalla muestra el avance y el resultado. */
  const [corrida, setCorrida] = useState<
    { fase: 'analizando'; paso: PasoDeImportacion } | { fase: 'listo'; resultado: ResultadoDeImportacion } | null
  >(null);
  const importar = async () => {
    if (!archivo || !plan || !hayQueHacer || !companyId || corrida) return;
    setError(null);
    setCorrida({ fase: 'analizando', paso: { paso: 'preparando' } });
    const {
      data: { user },
    } = await supabase.auth.getUser();
    const resultado = await correrImportacion({
      plan,
      companyId,
      brandId,
      userId: user?.id,
      archivo: archivo.name,
      alAvanzar: (paso) => setCorrida({ fase: 'analizando', paso }),
    });
    setCorrida({ fase: 'listo', resultado });
  };
  const otraVez = () => {
    setCorrida(null);
    quitar();
  };

  if (corrida?.fase === 'analizando') return <Analizando paso={corrida.paso} archivo={archivo?.name ?? ''} />;
  if (corrida?.fase === 'listo') {
    return <Resultado resultado={corrida.resultado} onVer={() => navigate('/lanzamientos')} onOtra={otraVez} />;
  }

  const casilla = (texto: string, valor: boolean, cambiar: (v: boolean) => void, nota: string | null, deshabilitada = false) => (
    <label className={`flex items-start gap-2.5 text-sm ${deshabilitada ? 'cursor-not-allowed text-neutral-400' : 'cursor-pointer text-brand-black'}`}>
      <input
        type="checkbox"
        checked={valor && !deshabilitada}
        disabled={deshabilitada}
        onChange={(e) => cambiar(e.target.checked)}
        className="mt-0.5 h-4 w-4 shrink-0 rounded border-neutral-300 accent-[rgb(var(--brand-primary-rgb))]"
      />
      <span>
        {texto}
        {nota && <span className="block text-xs text-neutral-500">{nota}</span>}
      </span>
    </label>
  );

  return (
    <div className="mt-6 rounded-2xl border border-neutral-200 bg-surface-card p-5 shadow-sm sm:p-6">
      <p className="text-sm text-neutral-600">
        Subí un Excel o un .csv con tus productos, con las columnas de Tiendanube. Los nuevos se crean y los que ya
        están se actualizan; llegan a tu tienda cuando sincronizás. Podés usar la planilla que baja "Exportar" o la plantilla de abajo.
      </p>
      {/* La plantilla de Tiendanube, para usar como base: un toque y se baja. */}
      <a
        href="/plantillas/plantilla-productos.csv"
        download="plantilla-productos.csv"
        className="group mt-4 flex items-center gap-3 rounded-xl border border-[rgb(var(--brand-primary-rgb)/0.4)] bg-[rgb(var(--brand-primary-rgb)/0.05)] p-3 transition-colors hover:bg-[rgb(var(--brand-primary-rgb)/0.09)]"
      >
        <span className="grid h-11 w-11 shrink-0 place-items-center rounded-lg bg-[rgb(var(--brand-primary-rgb)/0.12)] text-brand-primary">
          <FileSpreadsheet className="h-5 w-5" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-semibold text-brand-black">Descargar plantilla</span>
          <span className="block text-xs text-neutral-600">Un .csv vacío con las columnas de Tiendanube, para completar.</span>
        </span>
        <ArrowDownToLine className="h-5 w-5 shrink-0 text-brand-primary transition-transform group-hover:translate-y-0.5" />
      </a>

      <input
        ref={entrada}
        type="file"
        accept=".xlsx,.csv,.tsv"
        className="hidden"
        onChange={(e) => {
          elegir(e.target.files?.[0]);
          e.target.value = '';
        }}
      />
      {archivo ? (
        <div className="mt-5 rounded-xl border border-neutral-200 bg-white p-3">
          <div className="flex items-center gap-3">
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-emerald-50 text-emerald-600">
              <FileSpreadsheet className="h-5 w-5" />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-medium text-brand-black">{archivo.name}</span>
              <span className="block text-xs text-neutral-500">
                {analisis?.estado === 'leyendo'
                  ? 'Leyendo y comparando con tu tienda…'
                  : analisis?.estado === 'listo'
                    ? `${plural(analisis.planilla.productos.length, 'producto', 'productos')}: ${plural(
                        analisis.planilla.productos.length - yaEstan - ambiguos.length,
                        'nuevo',
                        'nuevos',
                      )} y ${yaEstan} que ya ${yaEstan === 1 ? 'está' : 'están'} en tu tienda${
                        ambiguos.length > 0 ? ` (${ambiguos.length} sin identificar)` : ''
                      }`
                    : `${Math.max(1, Math.round(archivo.size / 1024))} KB`}
              </span>
            </span>
            {analisis?.estado === 'leyendo' && <Loader2 className="h-4 w-4 animate-spin text-neutral-400" />}
            <button
              type="button"
              onClick={quitar}
              aria-label="Quitar el archivo"
              className="grid h-8 w-8 place-items-center rounded-lg text-neutral-400 hover:bg-neutral-100 hover:text-brand-black"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          {ambiguos.length > 0 && (
            <p className="mt-2 text-xs text-amber-700">
              {ambiguos.length === 1 ? `"${ambiguos[0]}" tiene` : `${ambiguos.length} productos tienen`} un nombre que se repite en
              tu tienda y no traen su ID ni su identificador de URL: no se importan, para no duplicarlos.
            </p>
          )}
          {analisis?.estado === 'error' && (
            <p role="alert" className="mt-2 text-sm text-red-600">
              {analisis.texto}
            </p>
          )}
        </div>
      ) : (
        <button
          type="button"
          onClick={() => entrada.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setArrastrando(true);
          }}
          onDragLeave={() => setArrastrando(false)}
          onDrop={(e) => {
            e.preventDefault();
            setArrastrando(false);
            elegir(e.dataTransfer.files?.[0]);
          }}
          className={`mt-5 flex w-full flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed py-8 px-4 text-sm font-medium transition-colors ${
            arrastrando
              ? 'border-brand-primary bg-[rgb(var(--brand-primary-rgb)/0.05)] text-brand-primary'
              : 'border-neutral-300 bg-neutral-50 text-neutral-500 hover:border-neutral-400 hover:text-brand-black'
          }`}
        >
          <span className="grid h-10 w-10 place-items-center rounded-full border-2 border-current">
            <Plus className="h-5 w-5" />
          </span>
          Cargar archivo .xlsx o .csv
          <span className="text-xs font-normal text-neutral-400">o arrastralo acá</span>
        </button>
      )}

      <div className="mt-5 space-y-3">
        {casilla(
          'Modificar productos ya existentes',
          modificar,
          setModificar,
          analisis?.estado !== 'listo'
            ? 'Los que ya están en tu tienda (mismo identificador de URL o nombre) se actualizan con los datos de la planilla.'
            : yaEstan === 0
              ? 'Ninguno de la planilla está en tu tienda: todos se crean.'
              : modificar
                ? `${plural(yaEstan, 'producto se actualiza', 'productos se actualizan')} con los datos de la planilla.`
                : `${plural(yaEstan, 'producto que ya está se deja', 'productos que ya están se dejan')} como está${yaEstan === 1 ? '' : 'n'}.`,
        )}
        {casilla(
          'Actualizar stock',
          stock,
          setStock,
          sinStock
            ? 'La planilla no tiene columna de stock.'
            : stock
              ? 'Se usa el stock de la planilla.'
              : 'Se ignora la columna Stock: el stock de tu tienda no cambia.',
          sinStock,
        )}
      </div>

      {error && (
        <p role="alert" className="mt-4 text-sm text-red-600">
          {error}
        </p>
      )}
      {plan && !hayQueHacer && (
        <p className="mt-4 text-sm text-amber-700">
          Con estas opciones no queda nada para importar: todos los productos ya están en tu tienda.
        </p>
      )}
      <button
        type="button"
        onClick={() => void importar()}
        disabled={!hayQueHacer}
        className={boton('primario', { extra: 'mt-6' })}
      >
        <Upload className="h-4 w-4" />
        Importar
      </button>
    </div>
  );
}

/** Mientras corre: los pasos, con el que está en curso y una barra. */
function Analizando({ paso, archivo }: { paso: PasoDeImportacion; archivo: string }) {
  const pasos = [
    { id: 'preparando', texto: 'Leyendo la planilla y comparando con tu tienda' },
    { id: 'creando', texto: 'Creando los productos nuevos' },
    { id: 'modificando', texto: 'Actualizando los que ya están' },
  ] as const;
  const actual = pasos.findIndex((x) => x.id === paso.paso);
  const avance = paso.paso === 'preparando' ? 0.05 : paso.total > 0 ? paso.hechos / paso.total : 1;
  return (
    <div className="mt-6 rounded-2xl border border-neutral-200 bg-surface-card p-6 shadow-sm sm:p-8" aria-live="polite">
      <div className="flex items-center gap-3">
        <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-[rgb(var(--brand-primary-rgb)/0.08)] text-brand-primary">
          <Loader2 className="h-5 w-5 animate-spin" />
        </span>
        <div className="min-w-0">
          <p className="text-base font-semibold text-brand-black">Importando {archivo}</p>
          <p className="text-sm text-neutral-500">Podés seguir usando Atlas: te avisamos en notificaciones cuando termine.</p>
        </div>
      </div>
      <div className="mt-6 h-1.5 overflow-hidden rounded-full bg-neutral-100">
        <div
          className="h-full rounded-full bg-brand-primary transition-[width] duration-500 ease-out"
          style={{ width: `${Math.round(((Math.max(0, actual) + avance) / pasos.length) * 100)}%` }}
        />
      </div>
      <ol className="mt-5 space-y-3">
        {pasos.map((x, i) => {
          const hecho = i < actual;
          const enCurso = i === actual;
          return (
            <li key={x.id} className={`flex items-center gap-3 text-sm ${hecho || enCurso ? 'text-brand-black' : 'text-neutral-400'}`}>
              <span
                className={`grid h-6 w-6 shrink-0 place-items-center rounded-full ${
                  hecho ? 'bg-emerald-500 text-white' : enCurso ? 'bg-[rgb(var(--brand-primary-rgb)/0.1)] text-brand-primary' : 'bg-neutral-100'
                }`}
              >
                {hecho ? <Check className="h-3.5 w-3.5" strokeWidth={3} /> : enCurso ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
              </span>
              {x.texto}
              {enCurso && paso.paso !== 'preparando' && paso.total > 0 && (
                <span className="ml-auto tabular-nums text-xs text-neutral-500">
                  {paso.hechos} de {paso.total}
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
}

/** Cómo terminó. Lo mismo queda en la campana. */
function Resultado({ resultado: r, onVer, onOtra }: { resultado: ResultadoDeImportacion; onVer: () => void; onOtra: () => void }) {
  const hechos = r.creados + r.modificados;
  const conErrores = r.fallas.length > 0;
  const titulo = hechos === 0 ? 'No se pudo importar' : conErrores ? 'Importado, con errores' : 'Listo, planilla importada';
  return (
    <div className="mt-6 rounded-2xl border border-neutral-200 bg-surface-card p-6 shadow-sm sm:p-8">
      <span
        className={`grid h-12 w-12 place-items-center rounded-2xl ${
          hechos === 0 ? 'bg-red-50 text-red-600' : conErrores ? 'bg-amber-50 text-amber-600' : 'bg-emerald-50 text-emerald-600'
        }`}
      >
        {hechos === 0 || conErrores ? <AlertTriangle className="h-6 w-6" /> : <Check className="h-6 w-6" strokeWidth={2.5} />}
      </span>
      <p className="mt-4 text-lg font-semibold text-brand-black">{titulo}</p>
      <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
        {[
          ['Nuevos', r.creados],
          ['Actualizados', r.modificados],
          ['Sin tocar', r.salteados],
          ['Con error', r.fallas.length],
        ].map(([t, n]) => (
          <div key={t} className="rounded-xl bg-neutral-50 py-3 px-3.5">
            <p className="text-2xl font-semibold tabular-nums text-brand-black">{n}</p>
            <p className="text-xs text-neutral-500">{t}</p>
          </div>
        ))}
      </div>
      {conErrores && (
        <ul className="mt-4 space-y-1 rounded-xl bg-red-50 py-3 px-4 text-sm text-red-700">
          {r.fallas.slice(0, 8).map((f) => (
            <li key={f}>{f}</li>
          ))}
          {r.fallas.length > 8 && <li>Y {r.fallas.length - 8} más.</li>}
        </ul>
      )}
      {r.notas.length > 0 && (
        <ul className="mt-4 space-y-1 rounded-xl bg-amber-50 py-3 px-4 text-sm text-amber-800">
          {r.notas.slice(0, 8).map((n) => (
            <li key={n}>{n}</li>
          ))}
          {r.notas.length > 8 && <li>Y {r.notas.length - 8} más.</li>}
        </ul>
      )}
      {hechos > 0 && (
        <p className="mt-4 text-sm text-neutral-600">
          Quedaron como "Sin sincronizar" en tu lista: revisalos, sumales fotos a los nuevos y tocá Sincronizar para que
          lleguen a tu tienda.
        </p>
      )}
      <div className="mt-6 flex flex-wrap gap-2">
        <button type="button" onClick={onVer} className={boton('primario')}>
          Ver productos
        </button>
        <button type="button" onClick={onOtra} className={boton('secundario')}>
          Importar otra planilla
        </button>
      </div>
    </div>
  );
}
